import React, { useEffect, useMemo, useState } from 'react';
import WbSunnyOutlinedIcon from '@mui/icons-material/WbSunnyOutlined';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';

const STORAGE_KEY = 'admin-theme';
const THEME_CHANGE_EVENT = 'admin-theme-change';

const themeOptions = [
    {
        value: 'light',
        label: 'Светлая',
        short: 'Свет',
        icon: <WbSunnyOutlinedIcon fontSize="small" />,
    },
    {
        value: 'dark',
        label: 'Тёмная',
        short: 'Тёмн',
        icon: <DarkModeOutlinedIcon fontSize="small" />,
    },
    {
        value: 'white',
        label: 'Белая',
        short: 'Бел',
        icon: <WbSunnyOutlinedIcon fontSize="small" />,
    },
    {
        value: 'black',
        label: 'Чёрная',
        short: 'Чёрн',
        icon: <AutoAwesomeOutlinedIcon fontSize="small" />,
    },
];

const normalizeTheme = (value) => {
    const prepared = String(value || '').trim();

    if (prepared === 'light-AO') return 'light';
    if (prepared === 'dark-AO') return 'dark';

    if (themeOptions.some((item) => item.value === prepared)) {
        return prepared;
    }

    return 'light';
};

const readSavedTheme = () => {
    if (typeof window === 'undefined') return '';

    try {
        return window.localStorage.getItem(STORAGE_KEY) || '';
    } catch (error) {
        return '';
    }
};

const getInitialTheme = (userTheme) => {
    const saved = readSavedTheme();

    return saved
        ? normalizeTheme(saved)
        : normalizeTheme(userTheme);
};

const applyTheme = (theme) => {
    if (typeof document === 'undefined') return;

    const safeTheme = normalizeTheme(theme);
    const darkTheme = safeTheme === 'dark' || safeTheme === 'black';

    document.documentElement.setAttribute('data-theme', safeTheme);
    document.documentElement.classList.remove('light-AO', 'dark-AO');
    document.documentElement.classList.add(darkTheme ? 'dark-AO' : 'light-AO');
    document.documentElement.style.colorScheme = darkTheme ? 'dark' : 'light';

    try {
        window.localStorage.setItem(STORAGE_KEY, safeTheme);
    } catch (error) {
        // Тема продолжает работать до перезагрузки даже без localStorage.
    }

    window.dispatchEvent(new CustomEvent(THEME_CHANGE_EVENT, {
        detail: { theme: safeTheme },
    }));
};

const ThemeSwitcher = ({ userThemeType }) => {
    const [theme, setTheme] = useState(() => getInitialTheme(userThemeType));

    const activeTheme = useMemo(() => {
        return themeOptions.find((item) => item.value === theme)
            || themeOptions[0];
    }, [theme]);

    useEffect(() => {
        applyTheme(theme);
    }, [theme]);

    useEffect(() => {
        const handleThemeChange = (event) => {
            const nextTheme = normalizeTheme(event?.detail?.theme);

            setTheme((previous) => (
                previous === nextTheme ? previous : nextTheme
            ));
        };

        const handleStorage = (event) => {
            if (event.key !== STORAGE_KEY || !event.newValue) return;

            const nextTheme = normalizeTheme(event.newValue);

            setTheme((previous) => (
                previous === nextTheme ? previous : nextTheme
            ));
        };

        window.addEventListener(THEME_CHANGE_EVENT, handleThemeChange);
        window.addEventListener('storage', handleStorage);

        return () => {
            window.removeEventListener(THEME_CHANGE_EVENT, handleThemeChange);
            window.removeEventListener('storage', handleStorage);
        };
    }, []);

    return (
        <div
            className="themeSwitcher"
            title={`Тема: ${activeTheme.label}`}
            data-theme-value={theme}
        >
            <div className="themeSwitcher__compact">
                {activeTheme.icon}
                <span>{activeTheme.short}</span>
            </div>

            <select
                className="themeSwitcher__select"
                value={theme}
                onChange={(event) => setTheme(normalizeTheme(event.target.value))}
                aria-label="Переключить тему админки"
            >
                {themeOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                        {option.label}
                    </option>
                ))}
            </select>
        </div>
    );
};

export default ThemeSwitcher;
